import JSZip from 'jszip';
import * as THREE from 'three';
import { Text as TroikaText } from 'troika-three-text';
import { serializeScenes } from './sceneSerializer';
import { getDefaultDocuments } from '../../../constants/projectTypes';
import { OUTPUT_CATEGORIES } from '../../../config/panelConfig/outputCategories';
import { OUTPUT_TYPES } from '../../../config/panelConfig/outputTypes';
import { PANEL_TYPES } from '../../../config/panelConfig/panelTypes';

// ─── Format Constants ────────────────────────────────────────────────────────
const FORMAT_VERSION = '1.0.0';
const APP_VERSION    = '0.8.3';

// ─── Debug Logging (HAFİF — pahalı bbox/matrix yok) ──────────────────────────
// Save akışını detaylı logla. Tanı amaçlı; true yapılınca tüm timing/size
// log'ları console'a düşer. Sadece performance.now() + JSON.length kullanır,
// pahalı node traversal yok.
const SAVE_DEBUG = false;
const _t = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const _fmtMs = (ms) => (ms < 1000 ? `${ms.toFixed(0)}ms` : `${(ms / 1000).toFixed(2)}s`);
const _fmtBytes = (b) => {
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / 1024 / 1024).toFixed(2)}MB`;
};

// ─── Tab ID ↔ Discipline Code Mapping ────────────────────────────────────────
// scenesRef.current key'i ya legacy ID (mimari/elektrik/...) ya da backend
// code (strong_current/weak_current/...) olabilir — resolveTabId mantığı
// projectTabs slice'tan geliyor. Save ve load aynı discipline'i bulmalı, bu
// yüzden HER İKİ değişkeni mapping'e dahil et.
const TAB_TO_DISCIPLINE = {
  mimari:             'architectural',
  architectural:      'architectural',
  elektrik:           'electrical',
  strong_current:     'electrical',
  telekom:            'weak_current',
  weak_current:       'weak_current',
  yangin:             'fire_detection',
  fire_detection:     'fire_detection',
  santiye:            'construction_site',
  construction_site:  'construction_site',
  baglanti:           'connection_line',
  connection_line:    'connection_line',
};
// DISCIPLINE_TO_TAB: discipline kodundan legacy tab ID'ye geri (load için).
// Object.fromEntries duplicate value'da sonuncuyu alır — legacy ID'leri
// (mimari/elektrik/...) son ekleyerek tercih ettiriyoruz. Restore tarafında
// scenesRef'e koymak için legacy ID güvenli; useTabSwitch resolveTabId ile
// backend code'a çevirir.
const DISCIPLINE_TO_TAB = {
  architectural:      'mimari',
  electrical:         'elektrik',
  weak_current:       'telekom',
  fire_detection:     'yangin',
  construction_site:  'santiye',
  connection_line:    'baglanti',
};

// Discipline metadata (label, hasInfrastructure)
const DISCIPLINE_META = {
  architectural:     { label: 'Mimari',              hasInfrastructure: false },
  electrical:        { label: 'Kuvvetli Akım',       hasInfrastructure: true  },
  weak_current:      { label: 'Zayıf Akım',          hasInfrastructure: true  },
  fire_detection:    { label: 'Yangın Algılama',      hasInfrastructure: true  },
  construction_site: { label: 'Şantiye',              hasInfrastructure: false },
  connection_line:   { label: 'Bağlantı Hattı',       hasInfrastructure: false },
};

// Component kind → spec componentType mapping
const KIND_TO_COMPONENT_TYPE = {
  supply:        'main_distribution_panel',
  meter_panel:   'meter_panel',
  dist_panel:    'distribution_panel',
  panel:         'distribution_panel',
  load:          'load',
  branch:        'branch',
  socket_line:   'socket_outlet',
  machine_line:  'machine',
  switch_line:   'wall_switch',
};

// ─── Redux Slice Extractors (for _state/ backup) ────────────────────────────
const SLICE_MAP = {
  'dxf-data':         s => s.dxf,
  'layers':           s => s.layer,
  'simple-supply':    s => ({
    feeders:    s.simpleSupply.feeders,
    components: s.simpleSupply.components,
    metadata:   s.simpleSupply.metadata,
  }),
  'wires':            s => s.wires,
  'floor-plans':      s => {
    const tabs = s.floorPlans.tabs || {};
    const serializedTabs = {};
    for (const [tabId, tabState] of Object.entries(tabs)) {
      serializedTabs[tabId] = {
        byId: Object.fromEntries(
          Object.entries(tabState.byId || {}).map(([k, v]) => {
            // Cross-tab plan'lar (non-mimari tab'lardaki kopyalar): plan.group
            // referansı mimari'den paylaşılıyor (floorPlanSlice.copyPlanToTab).
            // _serializedGroup'u 4 tab'a 4 kez yazmak save'i 4x yavaşlatır ve
            // ~100MB ekstra memory baskısı yaratır (kullanıcı Out of Memory
            // hatası aldı). Cross-tab plan'larda group=null yaz; reload'da
            // sourceFloorPlanId'den mimari'nin group referansı alınır.
            const isCrossTab = tabId !== 'mimari' && v.sourceFloorPlanId;
            return [k, {
              ...v,
              objects: undefined,
              group: undefined,
              _serializedGroup: isCrossTab
                ? null  // sourceFloorPlanId üzerinden reload'da mimari'den alınır
                : (v.group ? serializeFloorPlanGroup(v.group) : null),
            }];
          })
        ),
        allIds: tabState.allIds || [],
        activeId: tabState.activeId || null,
      };
    }
    return {
      activeTabId: s.floorPlans.activeTabId || 'mimari',
      tabs: serializedTabs,
    };
  },
  'grounding':        s => s.grounding,
  'baslik':           s => s.baslik,
  // baslik-templates: kullanıcı-seviyesi (backend'de saklanır) — projeye gömülmez.
  // Sadece projenin seçtiği activeTemplateId baslik slice'ında zaten bulunur.
  'panel-config':     s => s.panelConfig,
  'panel-detail':     s => s.panelDetail,
  'energy-room':      s => {
    const er = s.energyRoom;
    return {
      panels: er.panels,
      clipRect: er.clipRect,
      clippedSegments: er.clippedSegments,
      customDimensions: er.customDimensions,
      confirmed: er.confirmed,
      floorPlanId: er.floorPlanId,
      wallClearanceCm: er.wallClearanceCm,
      panelDepthCm: er.panelDepthCm,
      drawingScale: er.drawingScale,
      dimTextSize: er.dimTextSize,
      dimArrowSize: er.dimArrowSize,
      dimExtInner: er.dimExtInner,
      dimExtOuter: er.dimExtOuter,
      dimLineSpacing: er.dimLineSpacing,
      customDimTextSize: er.customDimTextSize,
      customDimArrowSize: er.customDimArrowSize,
      frameMargin: er.frameMargin,
      panoDetayConfig: er.panoDetayConfig,
    };
  },
  'project-type':     s => s.projectType,
  'settings':         s => s.settings,
  'mods':             s => s.mods,
  'project-tabs':     s => s.projectTabs,
  'tesisat':          s => s.tesisat,
  'utils':            s => {
    const sp = s.utils.sitePlan;
    return {
      updateCounter: s.utils.updateCounter,
      ...(sp?.width ? {
        sitePlan: {
          name: sp.name,
          type: sp.type,
          width: sp.width,
          height: sp.height,
          objectsIds: sp.objectsIds,
          _serializedGroup: sp.group ? serializeFloorPlanGroup(sp.group) : null,
        },
      } : {}),
    };
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Wire-like obj tespiti: sahnedeki tüm Line/Polyline'ları gezerken sadece
// gerçek elektriksel wire'ları yakalamak için kullanılır. Yanlış kapsam:
// - dar → wire kaybı (data loss)
// - geniş → fazla obj wire olarak save edilir (restore'da karışıklık)
function isWireLike(obj) {
  if (!obj?.userData?.id) return false;
  // DXF kaynaklı veya kullanıcı-çizimi (CAD primitif) objeler wire DEĞİLDİR.
  // Bunlar kendi yollarından restore edilir (dxf-data.json / floor-plans.json).
  // KÖK NEDEN: DXF kat planı polyline'ları userData.type === 'polyline' taşıyor;
  // aşağıdaki type==='polyline' kontrolü bunları wire sanıp wires.json'a
  // yazıyordu (kayıt-11'de 2891 DXF çizgisi). Load'da useWireRebuild bunları
  // elektrik sahnesine döküp "489 mimari çöp" yaratıyordu. Gerçek elektrik
  // wire'ları wiring hook'larından gelir; source==='dxf'/_userDrawn taşımaz.
  if (obj.userData.source === 'dxf') return false;
  if (obj.userData._userDrawn === true) return false;
  const id = obj.userData.id;
  if (typeof id === 'string') {
    // Wire ID prefix'leri: wireMainFeeder, wirePanelFeeder, feederSocket,
    // wireMachine, feederLighting, wireLightingFixture, wireBell, vs.
    if (/^(wire|feeder)/i.test(id)) return true;
  }
  const t = obj.userData.type;
  if (typeof t === 'string') {
    if (t === 'polyline' || t === 'curvedLine' || t === 'floorTransitionWire') return true;
    if (t === 'feederWire' || t.startsWith('wire')) return true;
  }
  return false;
}

// Wire vertex'leri save için her zaman WORLD koord olarak yazılır
// (mevcut JBZ formatı bu şekilde — fpMesh-LOCAL'a girince scene değişikliği
// olur ama save'de world pos tutarlı).
function readVerticesWorld(obj) {
  const posAttr = obj?.geometry?.getAttribute?.('position');
  if (!posAttr) return [];
  obj.updateMatrixWorld(true);
  const mw = obj.matrixWorld;
  const arr = posAttr.array;
  const vertices = [];
  const v = new THREE.Vector3();
  for (let i = 0; i < arr.length; i += 3) {
    v.set(arr[i], arr[i + 1], arr[i + 2]).applyMatrix4(mw);
    vertices.push({ x: v.x, y: v.y, z: v.z });
  }
  return vertices;
}

function getMaterialColorHex(obj) {
  const mat = Array.isArray(obj?.material) ? obj.material[0] : obj?.material;
  if (!mat?.color) return '#ffffff';
  return '#' + mat.color.getHexString();
}

// ID'den scene mesh bul. Her tesisat öğesi userData.id taşır.
// Multi-tab durumu: scenes bir { tabId → Scene } map'i. Tüm tab'larda
// gez (aynı id farklı tab'da olabilir teorik olarak — pratikte unique).
function findMeshById(scenes, id) {
  if (!scenes || !id) return null;
  const sceneList = Array.isArray(scenes) ? scenes : Object.values(scenes);
  for (const scene of sceneList) {
    if (!scene || typeof scene.traverse !== 'function') continue;
    let found = null;
    scene.traverse((obj) => {
      if (found) return;
      if (obj.userData?.id === id) found = obj;
    });
    if (found) return found;
  }
  return null;
}

// Tesisat (socket, fixture, machine, panel, supplyPoint, cableShaft, vs)
// save için Redux yerine SAHNE'den okur — single source of truth.
//
// MİMARİ KARAR: mesh sahnede NEREDEYSE oraya kaydedilir. Mesh parent'ı
// fpMesh ise pos LOCAL + _v=3 yazılır; scene altındaysa pos WORLD yazılır.
// Bu sayede Redux'taki stale _v veya pos önem taşımaz — kayıt mesh'in
// gerçek o anki haline göre yapılır.
function collectTesisatFromScene(scenes, reduxState) {
  const reduxTesisat = reduxState.tesisat || {};
  const reduxById = reduxTesisat.byId || {};
  const allIds = reduxTesisat.allIds || Object.keys(reduxById);
  const byId = {};
  const wpos = new THREE.Vector3();
  for (const id of allIds) {
    const item = reduxById[id];
    if (!item) continue;
    const mesh = findMeshById(scenes, id);
    if (!mesh) {
      // Mesh sahnede yoksa redux item'ı olduğu gibi koru (silinmiş olabilir
      // ama henüz Redux'tan temizlenmedi — restore tarafı yokluğu handle eder).
      byId[id] = item;
      continue;
    }

    // Pos + _v: mesh parent durumuna göre
    const isInFpMesh = mesh.parent?.userData?.isFloorPlanMesh === true;
    let pos;
    let _v;
    let floorPlanId = item.floorPlanId;
    if (isInFpMesh) {
      pos = { x: mesh.position.x, y: mesh.position.y, z: mesh.position.z };
      _v = 3;
      // Mesh'in gerçek floorPlanId'sini parent fpMesh'ten çek (item'dakı stale olabilir)
      floorPlanId = mesh.parent.userData.floorPlanId || floorPlanId;
    } else {
      mesh.getWorldPosition(wpos);
      pos = { x: wpos.x, y: wpos.y, z: wpos.z };
      _v = item._v; // mevcut versiyonu koru (eski format / new format)
    }

    // Rotation: 2D çizim → genelde Z ekseninde. mesh.rotation.z LOCAL,
    // fpMesh translate-only ise WORLD ile aynı.
    const rot = (typeof item.rotation === 'number' || item.rotation === undefined || item.rotation === null)
      ? (mesh.rotation?.z ?? 0)
      : {
          x: mesh.rotation?.x ?? 0,
          y: mesh.rotation?.y ?? 0,
          z: mesh.rotation?.z ?? 0,
        };

    byId[id] = {
      ...item,
      position: pos,
      rotation: rot,
      ...(floorPlanId !== undefined ? { floorPlanId } : {}),
      ...(_v !== undefined ? { _v } : {}),
    };
  }
  return { ...reduxTesisat, byId, allIds };
}

// Sahnedeki tüm wire-like obj'leri toplar — Redux yerine scene OTORİTE.
// Save anında her wire'ın güncel vertex'leri (kullanıcının düzenlemesi
// dahil) doğrudan geometry'den okunur. Redux'taki vertex'ler stale olsa
// bile save doğru çıkar.
function collectWiresFromScene(scenes) {
  const byId = {};
  const allIds = [];
  if (!scenes) return { byId, allIds };
  const sceneList = Array.isArray(scenes) ? scenes : Object.values(scenes);
  for (const scene of sceneList) {
    if (!scene || typeof scene.traverse !== 'function') continue;
    scene.traverse((obj) => {
      if (!isWireLike(obj)) return;
      const id = obj.userData.id;
      if (byId[id]) return; // duplicate koruması (multi-tab/same id)
      byId[id] = {
        id,
        type: obj.userData.type || 'polyline',
        vertices: readVerticesWorld(obj),
        layer: obj.userData.layer || obj.userData.parent || 'default',
        color: getMaterialColorHex(obj),
        // Three.js mesh uuid'sini sakla: snap sistemi (getSnapPointsFromScene /
        // snapWallHandler) wire mesh'ini Redux'taki sceneUUID ile getObjectByProperty
        // yapıp bulur. Kaydedilmezse load'da rebuild yeni rastgele uuid alır, snap
        // wire'ı bulamaz → tesisat çiziminde wire üzerinde snap/klavuz çizgileri oluşmaz.
        sceneUUID: obj.uuid,
        userData: { ...obj.userData },
      };
      allIds.push(id);
    });
  }
  return { byId, allIds };
}

function safeJSON(data) {
  // Circular reference + Three.js obj guard.
  // includeAll=true ile DXF/user-drawn objelerini dahil ederken bunların
  // userData'sında Three.js obj referansları (matrix, parent, children) veya
  // circular ref olabilir → JSON.stringify patlar → save fail.
  const seen = new WeakSet();
  return JSON.stringify(data, (key, value) => {
    if (value === null || value === undefined) return value;
    if (typeof value === 'function') return undefined;
    if (typeof value !== 'object') return value;
    // Three.js obj'leri skip — userData içinde mesh/material/geometry referansı olabilir
    if (value.isObject3D || value.isBufferGeometry || value.isMaterial
      || value.isTexture || value.isCamera || value.isLight) {
      return undefined;
    }
    // Circular reference koruması
    if (seen.has(value)) return undefined;
    seen.add(value);
    return value;
  }, 0);
}

function nowISO() {
  return new Date().toISOString();
}

/**
 * Build a SHA-256-like checksum placeholder.
 * Real SHA256 would require SubtleCrypto (async); we use a simple hash for now.
 */
function simpleChecksum(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + ch;
    hash |= 0;
  }
  return 'HASH_' + Math.abs(hash).toString(16).padStart(8, '0');
}

// ─── Floor Plan Group Serialization ──────────────────────────────────────────
// Serialize Three.js Group → JSON (does NOT skip DXF objects, unlike sceneSerializer)

function serializeFloorPlanGroup(group) {
  if (!group) return null;

  function ser(obj) {
    if (!obj) return null;
    if (obj.isLight || obj.isCamera) return null;

    const entry = {
      type: obj.type || 'Object3D',
      position: { x: obj.position?.x ?? 0, y: obj.position?.y ?? 0, z: obj.position?.z ?? 0 },
      rotation: { x: obj.rotation?.x ?? 0, y: obj.rotation?.y ?? 0, z: obj.rotation?.z ?? 0 },
      scale: { x: obj.scale?.x ?? 1, y: obj.scale?.y ?? 1, z: obj.scale?.z ?? 1 },
      visible: obj.visible ?? true,
      userData: {},
    };

    // Preserve important userData fields (skip non-serializable refs)
    if (obj.userData && typeof obj.userData === 'object') {
      for (const [key, val] of Object.entries(obj.userData)) {
        if (val === null || val === undefined) continue;
        const t = typeof val;
        if (t === 'string' || t === 'number' || t === 'boolean') {
          entry.userData[key] = val;
        } else if (Array.isArray(val)) {
          entry.userData[key] = val;
        }
      }
    }

    // TROIKA mtext: geometry clone'da sıfırlandığı için empty → aşağıdaki geometry
    // serialize'ı boş kalır ve deserialize case 'Mesh'te null dönüp troika DROP edilir
    // (kat planı text'leri kayıttan açılınca kayboluyordu). Ayrıca konum verisi
    // (bakedTextPos/position) OBJECT olduğu için yukarıdaki userData loop'una girmiyor.
    // Troika'yı metin verisiyle + konum object'leriyle açıkça serialize et; deserialize
    // bunu gerçek TroikaText olarak kurar, recreateTroikaText doğru konumlar.
    if (typeof obj.sync === 'function' && typeof obj.text === 'string') {
      entry.troikaText = {
        text: String(obj.text),
        fontSize: typeof obj.fontSize === 'number' ? obj.fontSize : undefined,
        color: (typeof obj.color === 'number' || typeof obj.color === 'string') ? obj.color : undefined,
        anchorX: obj.anchorX,
        anchorY: obj.anchorY,
      };
      if (obj.userData?.bakedTextPos) entry.userData.bakedTextPos = { ...obj.userData.bakedTextPos };
      if (obj.userData?.bakedTextScale != null) entry.userData.bakedTextScale = obj.userData.bakedTextScale;
      if (obj.userData?.position && typeof obj.userData.position === 'object') {
        entry.userData.position = { ...obj.userData.position };
      }
    }

    // matrixAutoUpdate=false (INSERT OCS): save raw matrix
    if (obj.matrixAutoUpdate === false && obj.matrix) {
      entry.matrix = Array.from(obj.matrix.elements);
      entry.matrixAutoUpdate = false;
    }

    // Geometry → flat positions array
    if (obj.geometry) {
      const posAttr = obj.geometry.getAttribute?.('position');
      if (posAttr) entry.geometryData = Array.from(posAttr.array);
      // Preserve index for indexed geometries (Mesh faces)
      if (obj.geometry.index) {
        entry.indexData = Array.from(obj.geometry.index.array);
      }
    }

    // Material
    const mat = Array.isArray(obj.material) ? obj.material[0] : obj.material;
    if (mat) {
      entry.materialData = {
        type: mat.type || 'LineBasicMaterial',
        color: mat.color ? mat.color.getHex() : 0xffffff,
        opacity: mat.opacity ?? 1,
        transparent: mat.transparent ?? false,
        linewidth: mat.linewidth,
        depthWrite: mat.depthWrite ?? true,
        side: mat.side ?? THREE.FrontSide,
      };
    }

    // Children
    if (obj.children?.length) {
      const children = obj.children.map(c => ser(c)).filter(Boolean);
      if (children.length > 0) entry.children = children;
    }

    return entry;
  }

  return ser(group);
}

// [SAVE-LOAD-REWRITE] export: JbzViewer (proje ön izleme) kat planı tabanını
// (_serializedGroup) bu deserializer ile açar — troikaText mtext + matrix/OCS
// desteği JbzViewer'ın eski kopyasında yoktu (kat planı yazıları kayboluyordu).
export function deserializeFloorPlanGroup(data) {
  if (!data) return null;

  function deser(d) {
    if (!d) return null;

    let obj = null;
    const type = d.type || 'Group';

    // TROIKA mtext: serialize'da troikaText olarak yazıldı (geometry kayıp). Gerçek
    // TroikaText olarak kur ki recreateTroikaText (setupKatPlani) onu bulup konumlasın.
    if (d.troikaText) {
      const t = new TroikaText();
      t.gpuAccelerateSDF = false; // GPU'da ANGLE_instanced_arrays yoksa hata → JS path'e zorla
      if (d.troikaText.text != null) t.text = d.troikaText.text;
      if (d.troikaText.fontSize != null) t.fontSize = d.troikaText.fontSize;
      if (d.troikaText.color != null) t.color = d.troikaText.color;
      if (d.troikaText.anchorX != null) t.anchorX = d.troikaText.anchorX;
      if (d.troikaText.anchorY != null) t.anchorY = d.troikaText.anchorY;
      try { t.sync(); } catch (_) { /* sync hatası kritik değil */ }
      obj = t;
      // transform + userData (bakedTextPos dahil) aşağıda restore edilir
      if (d.position) obj.position.set(d.position.x ?? 0, d.position.y ?? 0, d.position.z ?? 0);
      if (d.rotation) obj.rotation.set(d.rotation.x ?? 0, d.rotation.y ?? 0, d.rotation.z ?? 0);
      if (d.scale) obj.scale.set(d.scale.x ?? 1, d.scale.y ?? 1, d.scale.z ?? 1);
      obj.visible = d.visible ?? true;
      if (d.userData) obj.userData = { ...d.userData };
      if (Array.isArray(d.children)) {
        for (const childData of d.children) { const c = deser(childData); if (c) obj.add(c); }
      }
      return obj;
    }

    switch (type) {
      case 'Group':
      case 'Object3D':
        obj = new THREE.Group();
        break;
      case 'Sprite': {
        const mat = d.materialData
          ? new THREE.SpriteMaterial({
              color: d.materialData.color ?? 0xffffff,
              opacity: d.materialData.opacity ?? 1,
              transparent: d.materialData.transparent ?? false,
            })
          : new THREE.SpriteMaterial();
        obj = new THREE.Sprite(mat);
        break;
      }
      case 'Mesh': {
        const geom = createGeomFromData(d);
        if (!geom) return null;
        const mat = createMatFromData(d.materialData, 'Mesh');
        obj = new THREE.Mesh(geom, mat);
        break;
      }
      case 'LineSegments': {
        const geom = createGeomFromData(d);
        if (!geom) return null;
        const mat = createMatFromData(d.materialData, 'Line');
        obj = new THREE.LineSegments(geom, mat);
        break;
      }
      case 'LineLoop': {
        const geom = createGeomFromData(d);
        if (!geom) return null;
        const mat = createMatFromData(d.materialData, 'Line');
        obj = new THREE.LineLoop(geom, mat);
        break;
      }
      case 'Line':
      default: {
        if (!d.geometryData && !d.children?.length) {
          obj = new THREE.Group();
        } else if (d.geometryData) {
          const geom = createGeomFromData(d);
          if (!geom) return null;
          const mat = createMatFromData(d.materialData, 'Line');
          obj = new THREE.Line(geom, mat);
        } else {
          obj = new THREE.Group();
        }
        break;
      }
    }

    if (!obj) return null;

    // Restore matrix (INSERT OCS)
    if (d.matrixAutoUpdate === false && d.matrix) {
      obj.matrixAutoUpdate = false;
      obj.matrix.fromArray(d.matrix);
      obj.matrixWorldNeedsUpdate = true;
    } else {
      if (d.position) obj.position.set(d.position.x ?? 0, d.position.y ?? 0, d.position.z ?? 0);
      if (d.rotation) obj.rotation.set(d.rotation.x ?? 0, d.rotation.y ?? 0, d.rotation.z ?? 0);
      if (d.scale) obj.scale.set(d.scale.x ?? 1, d.scale.y ?? 1, d.scale.z ?? 1);
    }

    obj.visible = d.visible ?? true;
    if (d.userData) obj.userData = { ...d.userData };

    // Children
    if (Array.isArray(d.children)) {
      for (const childData of d.children) {
        const child = deser(childData);
        if (child) obj.add(child);
      }
    }

    return obj;
  }

  function createGeomFromData(d) {
    if (!d.geometryData || d.geometryData.length === 0) return null;
    // NaN/outlier-defense (bkz. sceneSerializer.js:createGeometry). DXF parse'ın
    // bozuk ürettiği polyline'lar (yarısı NaN vertex) JBZ'ye save ediliyor;
    // reload'da NaN-patch (0,0)'a çekip stretch duvar oluşturuyor. Bozuk
    // geometry'leri burada erkenden filtrele.
    const SCENE_OUTLIER_THRESHOLD = 8000;
    const positions = d.geometryData;
    let hasNaN = false;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i], y = positions[i + 1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        hasNaN = true;
        break;
      }
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    if (hasNaN) return null;
    if ((maxX - minX) > SCENE_OUTLIER_THRESHOLD || (maxY - minY) > SCENE_OUTLIER_THRESHOLD) {
      return null;
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.Float32BufferAttribute(d.geometryData, 3));
    if (d.indexData) {
      geom.setIndex(new THREE.BufferAttribute(new Uint32Array(d.indexData), 1));
    }
    return geom;
  }

  function createMatFromData(matData, hint) {
    if (!matData) {
      return hint === 'Mesh'
        ? new THREE.MeshBasicMaterial({ color: 0xffffff })
        : new THREE.LineBasicMaterial({ color: 0xffffff });
    }
    const opts = {
      color: matData.color ?? 0xffffff,
      opacity: matData.opacity ?? 1,
      transparent: matData.transparent ?? false,
      depthWrite: matData.depthWrite ?? true,
    };
    switch (matData.type) {
      case 'MeshBasicMaterial':
        return new THREE.MeshBasicMaterial({ ...opts, side: matData.side ?? THREE.FrontSide });
      case 'MeshStandardMaterial':
        return new THREE.MeshStandardMaterial({ ...opts, side: matData.side ?? THREE.FrontSide });
      case 'MeshLambertMaterial':
        return new THREE.MeshLambertMaterial({ ...opts, side: matData.side ?? THREE.FrontSide });
      case 'LineDashedMaterial':
        return new THREE.LineDashedMaterial({ ...opts, linewidth: matData.linewidth ?? 1 });
      case 'LineBasicMaterial':
      default:
        return new THREE.LineBasicMaterial({ ...opts, linewidth: matData.linewidth ?? 1 });
    }
  }

  return deser(data);
}

// ═══════════════════════════════════════════════════════════════════════════════
// SERIALIZE (Save)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Serialize Redux state + scenes into a spec-compliant .jbz ZIP.
 *
 * @param {object} reduxState - store.getState()
 * @param {object} meta       - { projectName, projectId }
 * @param {object|null} scenes - scenesRef.current (tabId → THREE.Scene)
 * @returns {Promise<Blob>}
 */
export async function serializeProject(reduxState, meta = {}, scenes = null) {
  const _tStart = SAVE_DEBUG ? _t() : 0;
  const _mark = (label) => { if (SAVE_DEBUG) console.log(`[save] @${_fmtMs(_t() - _tStart)} ${label}`); };
  if (SAVE_DEBUG) console.log(`[save] === BAŞLADI === scenes=${scenes ? Object.keys(scenes).length + ' tab' : 'null'}`);

  const zip = new JSZip();
  const now = nowISO();

  // ── Gather data from Redux ──
  const projectState  = reduxState.project || {};
  const projectTabs   = reduxState.projectTabs || {};
  const floorPlans    = reduxState.floorPlans || {};
  const simpleSupply  = reduxState.simpleSupply || {};
  const projectType   = reduxState.projectType || {};
  const settings      = reduxState.settings || {};
  const baslikTpl     = reduxState.baslikTemplate || {};

  const projectId   = meta.projectId || projectState.projectId || null;
  const projectName = meta.projectName || projectState.projectName || 'Yeni Proje';

  // Determine active discipline & enabled disciplines
  const activeTabId       = projectTabs.activeTabId || 'mimari';
  const enabledPaftaIds   = projectTabs.enabledPaftaIds || ['mimari'];
  const activeDiscipline  = TAB_TO_DISCIPLINE[activeTabId] || 'architectural';

  // Floor plans from mimari tab
  const mimariTab    = floorPlans.tabs?.mimari || { byId: {}, allIds: [] };
  const floorPlanIds = mimariTab.allIds || [];

  // ── 1. manifest.json (Section 3) ──
  const disciplineEntries = enabledPaftaIds
    .filter(id => TAB_TO_DISCIPLINE[id])
    .map(tabId => {
      const code = TAB_TO_DISCIPLINE[tabId];
      const dmeta = DISCIPLINE_META[code] || { label: code, hasInfrastructure: false };
      const tabData = floorPlans.tabs?.[tabId];
      const docCount = tabData ? (tabData.allIds || []).length : 0;
      return {
        code,
        label: dmeta.label,
        include: true,
        hasInfrastructure: dmeta.hasInfrastructure,
        documentCount: docCount,
      };
    });

  // Also include disabled disciplines with include: false
  Object.entries(TAB_TO_DISCIPLINE).forEach(([_tabId, code]) => {
    if (!disciplineEntries.find(d => d.code === code)) {
      const dmeta = DISCIPLINE_META[code] || { label: code, hasInfrastructure: false };
      disciplineEntries.push({
        code,
        label: dmeta.label,
        include: false,
        hasInfrastructure: dmeta.hasInfrastructure,
        documentCount: 0,
      });
    }
  });

  const floorPlanManifest = floorPlanIds.map((fpId, idx) => {
    const fp = mimariTab.byId[fpId];
    return {
      id: fpId,
      label: fp?.name || `Kat ${idx}`,
      type: fp?.type || 'floor',
      elevation: (fp?.number || 0) * 300,
    };
  });

  // Active floor plan document
  const activeFloorPlanTab = floorPlans.tabs?.[activeTabId];
  const activeDocId = activeFloorPlanTab?.activeId || null;

  const manifest = {
    formatVersion: FORMAT_VERSION,
    appVersion:    APP_VERSION,
    createdAt:     now,
    updatedAt:     now,
    projectId,
    projectName, // NOT in spec but useful for quick access
    checksum:      null, // Filled after all content is written

    activeState: {
      activeDiscipline,
      activeDocumentId: activeDocId,
      openDocuments: enabledPaftaIds
        .filter(id => id !== 'mimari' && floorPlans.tabs?.[id]?.activeId)
        .map(id => floorPlans.tabs[id].activeId),
      viewState: {
        zoom: 1.0,
        panX: 0,
        panY: 0,
      },
    },

    disciplines: disciplineEntries,
    floorPlans: floorPlanManifest,

    history: [
      {
        version: (projectState.currentVersion || 0) + 1,
        savedAt: now,
        savedBy: projectState.ownerId || null,
        changeLog: 'Proje kaydedildi',
      },
    ],
  };

  // ── 2. meta.json (Section 4) ──
  const metaJson = {
    project: {
      id:          projectId,
      name:        projectName,
      description: '',
      type:        projectType.type || projectType.projectType || 'residential',
      phase:       'design',
    },

    address: {
      country: 'TR',
      city: '',
      district: '',
      neighborhood: '',
      street: '',
      no: '',
      parcel: '',
      coordinates: null,
    },

    electrical: {
      totalInstalledPower: simpleSupply.metadata?.totalInstalledPower || 0,
      totalDemandPower:    simpleSupply.metadata?.totalDemandPower || 0,
      powerUnit:           'W',
      voltage:             380,
      frequency:           50,
      phases:              3,
      subscriberCount:     0,
      usageType:           projectType.type || 'residential',
      connectionType:      'underground',
      distributionCompany: '',
      applicationNo:       '',
    },

    stakeholders: [],
    standards: ['TS EN 60364', 'IEC 60364', 'TS 3994'],
    settings,
    createdAt: now,
    updatedAt: now,
  };

  // ── 3. permissions.json (Section 5) ──
  const permissionsJson = {
    version: '1.0',
    owner: {
      userId: projectState.ownerId || null,
      name:   '',
    },
    roles: [],
    publicAccess: 'none',
    signature: null,
  };

  // Write meta.json and permissions.json
  zip.file('meta.json', safeJSON(metaJson));
  zip.file('permissions.json', safeJSON(permissionsJson));

  // ── 4. floor-plans/ (Section 6) ──
  floorPlanIds.forEach((fpId) => {
    const fp = mimariTab.byId[fpId];
    if (!fp) return;
    const floorJson = {
      floorId:   fpId,
      label:     fp.name || 'Kat',
      type:      fp.type || 'floor',
      number:    fp.number || 0,
      elevation: (fp.number || 0) * 300,
      height:    280,
      unit:      'cm',
      floorCount: fp.floorCount || 1,

      frame: {
        width:     fp.width || 2400,
        height:    fp.height || 1600,
        paperSize: 'A1',
        scale:     '1/50',
      },

      layers: [],
      objects: [],
      // draw verisi _state/floor-plans.json'da saklanır (duplikasyon önlenir)
      sourceFloorPlanId: fp.sourceFloorPlanId || null,
    };
    zip.file(`floor-plans/${fpId}.json`, safeJSON(floorJson));
  });

  // ── 5. disciplines/ (Sections 7–10) ──
  enabledPaftaIds.forEach(tabId => {
    const code = TAB_TO_DISCIPLINE[tabId];
    if (!code) return;
    const dmeta = DISCIPLINE_META[code] || {};
    const tabData = floorPlans.tabs?.[tabId] || { byId: {}, allIds: [], activeId: null };
    const prefix = `disciplines/${code}`;

    // ── 5a. layout.json (Section 9) ──
    const placements = (tabData.allIds || []).map((docId, idx) => {
      const doc = tabData.byId[docId];
      return {
        documentId: docId,
        title:      doc?.name || `Doküman ${idx + 1}`,
        position:   { x: (idx % 3) * 900, y: Math.floor(idx / 3) * 650 },
        size:       { w: doc?.width || 841, h: doc?.height || 594 },
        zIndex:     idx + 1,
        visible:    true,
      };
    });

    const layoutJson = {
      disciplineCode: code,
      canvas: {
        width: 15000,
        height: 10000,
        backgroundColor: '#F5F5F5',
      },
      placements,
    };
    zip.file(`${prefix}/layout.json`, safeJSON(layoutJson));

    // ── 5b. infrastructure.json (Section 10) — only for disciplines that have it ──
    if (dmeta.hasInfrastructure && code === 'electrical') {
      const infraJson = buildElectricalInfrastructure(simpleSupply);
      zip.file(`${prefix}/infrastructure.json`, safeJSON(infraJson));
    }

    // ── 5c. documents/ (Section 8) ──
    (tabData.allIds || []).forEach(docId => {
      const doc = tabData.byId[docId];
      if (!doc) return;

      const floorPlanRef = (tabId !== 'mimari' && doc.sourceFloorPlanId)
        ? {
            sourceId: doc.sourceFloorPlanId,
            inheritGeometry: true,
            layerOverrides: {},
          }
        : null;

      const docJson = {
        documentId: docId,
        type:       tabId === 'mimari' ? 'floor_plan' : 'floor_plan',
        discipline: code,
        title:      doc.name || 'Doküman',
        revision:   'A',
        drawingNo:  '',

        frame: {
          width:       doc.width || 841,
          height:      doc.height || 594,
          paperSize:   'A1',
          orientation: 'landscape',
          scale:       '1/50',
          unit:        'mm',
        },

        coordinateSpace: {
          origin:     { x: 0, y: 0 },
          worldUnit:  'mm',
          pixelRatio: 1,
        },

        viewState: {
          zoom: 1.0,
          panX: 0,
          panY: 0,
          activeLayerId: null,
        },

        layers: [],
        objects: [],
        connections: [],
        annotations: [],

        titleBlock: null,
        floorPlanRef,

        // Hafif metadata — tam veri _state/floor-plans.json'da
        _appData: {
          sourceFloorPlanId: doc.sourceFloorPlanId || null,
          type: doc.type,
          number: doc.number,
          floorCount: doc.floorCount,
        },
      };

      // Determine filename
      const safeName = docId.replace(/[^a-zA-Z0-9_-]/g, '_');
      zip.file(`${prefix}/documents/${safeName}.json`, safeJSON(docJson));
    });
  });

  // ── 6. templates/ (Section 15) ──
  const systemRefs = {
    systemTemplates: [],
  };
  if (baslikTpl && baslikTpl.templates) {
    Object.entries(baslikTpl.templates || {}).forEach(([tplId, tpl]) => {
      systemRefs.systemTemplates.push({
        templateId: tplId,
        name:       tpl.name || tplId,
        category:   'title_block',
        appVersion: APP_VERSION,
        usedInDocuments: [],
        snapshot:   tpl,
      });
    });
  }
  zip.file('templates/system-refs.json', safeJSON(systemRefs));

  // ── 7. assets/ (Section 16) ──
  zip.file('assets/index.json', safeJSON({ assets: [] }));

  // Save aninda her kat plani documentGroup'unun world origin'ini + fpGroup
  // local pozisyonunu yakala. Load sirasinda fpGroup pozisyonu restore edilir,
  // DXF bbox non-determinizmi nedeniyle olusan kayma engellenir.
  let floorPlanOriginsAtSave = null;
  let fpGroupLocalPositions = null;
  if (scenes) {
    try {
      const origins = {};
      const fpGroupLocals = {};
      for (const tabKey of Object.keys(scenes)) {
        const scene = scenes[tabKey];
        if (!scene?.traverse) continue;
        scene.traverse(o => {
          const ud = o.userData;
          if (ud?.floorPlanId && ud?.docName === 'Kat Planı') {
            o.updateMatrixWorld(true);
            const m = o.matrixWorld.elements;
            origins[ud.floorPlanId] = { x: m[12], y: m[13] };
          }
          if (ud?.isLiveFloorPlanGroup && ud?.floorPlanId) {
            fpGroupLocals[ud.floorPlanId] = {
              x: o.position.x, y: o.position.y, z: o.position.z,
            };
          }
        });
      }
      if (Object.keys(origins).length > 0) {
        floorPlanOriginsAtSave = origins;
      }
      if (Object.keys(fpGroupLocals).length > 0) {
        fpGroupLocalPositions = fpGroupLocals;
      }
    } catch (err) {
      console.warn('[jbz-save] Failed to capture fp origins:', err);
    }
  }

  // Tesisat ANCHOR hesabı — DEVRE DIŞI (save hız optimizasyonu, 2026-05-19).
  // Her tesisat için sahnedeki en yakın DXF entity'sini bulup anchor inject
  // ediyordu (O(tesisat × DXF entity), her entity için updateMatrixWorld).
  // Büyük DXF'lerde save'in büyük yükü.
  //
  // Mesh-FP mimarisi (Aşama 3+) tesisat'ı fpMesh-child yapacak → tesisat
  // mesh-local koordinatlarda saklanacak → mesh sürüklendiğinde Three.js
  // parent matrix ile tesisat otomatik takip eder, anchor heuristiği gereksiz.
  // Eski kod referans için: git log -- src/services/projectSerializer.js

  // ── 8. _state/ — Raw Redux slices for perfect round-trip hydration ──
  _mark('_state/ döngüsü başlıyor');
  for (const [filename, extractor] of Object.entries(SLICE_MAP)) {
    try {
      const _tExt = SAVE_DEBUG ? _t() : 0;
      let data;
      if (filename === 'wires') {
        // Wire'lar Redux yerine SAHNE'den okunur — single source of truth.
        // Kullanıcı vertex sürüklemesi/gizmo/modifyMove geometry'yi günceller
        // ama Redux'a propagate etmez (sadece useFixtureGizmo dispatch ediyor).
        // Save Redux'tan okursa stale (default) vertex'leri kaydeder.
        data = collectWiresFromScene(scenes);
      } else if (filename === 'tesisat') {
        // Aynı prensip tesisat (socket/fixture/machine/panel/supply/cableShaft/
        // lightSwitch/buat vb) için: pos/rotation scene'den okunur. Drawing-time
        // mesh hareketi (gizmo, modifyMove, anchor) Redux'a yansımayabilir →
        // save'in scene'i kaynak alması tutarlılık sağlar.
        data = collectTesisatFromScene(scenes, reduxState);
      } else {
        data = extractor(reduxState);
      }
      const _tExtDone = SAVE_DEBUG ? _t() : 0;
      // Utils slice'a save-time fp origins'i inject et (scene-dependent veri)
      if (filename === 'utils' && (floorPlanOriginsAtSave || fpGroupLocalPositions)) {
        data = {
          ...data,
          ...(floorPlanOriginsAtSave ? { floorPlanOriginsAtSave } : {}),
          ...(fpGroupLocalPositions ? { fpGroupLocalPositions } : {}),
          floorPlanShiftCorrected: false,
        };
      }
      // Tesisat anchor inject — kaldırıldı (Mesh-FP refactor anchor'u
      // mesh-child mimarisiyle değiştiriyor).
      const json = safeJSON(data);
      const _tJSONDone = SAVE_DEBUG ? _t() : 0;
      zip.file(`_state/${filename}.json`, json);
      if (SAVE_DEBUG) {
        console.log(`[save]   [_state/${filename}.json] extractor=${_fmtMs(_tExtDone - _tExt)}, JSON=${_fmtMs(_tJSONDone - _tExtDone)}, size=${_fmtBytes(json.length)}`);
      }
    } catch (err) {
      console.warn(`[jbz-save] Failed to serialize _state/${filename}:`, err);
    }
  }
  _mark('_state/ döngüsü bitti');

  // Serialize per-tab Three.js scene objects
  // FAZ 1 (save-load-rewrite branch): Sahne snapshot'ı disiplin bazlı
  // klasörlerde kaydedilir.
  //
  // includeAll=false → DXF + user-drawn objeleri skip (mevcut sistem bunları
  // ayrı yoldan restore ediyor: dxf-data.json + useDxfScene). Dahil etmek
  // DUPLICATE yaratıyor → shader compile fail.
  // DXF dahil tam snapshot ileride (faz 2) ele alınacak — eski restore yolu
  // kapatıldıktan sonra includeAll=true güvenli olur.
  if (scenes) {
    try {
      const _tSS = SAVE_DEBUG ? _t() : 0;
      const sceneData = serializeScenes({ current: scenes });
      const _tSSDone = SAVE_DEBUG ? _t() : 0;
      if (sceneData && Object.keys(sceneData).length > 0) {
        // Eski format korunur (geri uyumluluk için)
        const fullJson = safeJSON(sceneData);
        zip.file('_state/scene-objects.json', fullJson);

        // YENİ FORMAT: tab başına ayrı dosya, disiplin koduyla
        // _state/<discipline>/scene.json
        for (const [tabId, objects] of Object.entries(sceneData)) {
          const discipline = TAB_TO_DISCIPLINE[tabId] || tabId;
          const tabJson = safeJSON({
            tabId,
            discipline,
            schema: 'scene-snapshot/v1',
            children: objects,
          });
          zip.file(`_state/${discipline}/scene.json`, tabJson);
          if (SAVE_DEBUG) {
            console.log(`[save]   [_state/${discipline}/scene.json] size=${_fmtBytes(tabJson.length)} (${objects.length} kök obje)`);
          }
        }

        if (SAVE_DEBUG) {
          const tabCount = Object.keys(sceneData).length;
          const totalRoots = Object.values(sceneData).reduce((a, arr) => a + arr.length, 0);
          console.log(`[save]   [scene-objects.json] serializeScenes=${_fmtMs(_tSSDone - _tSS)}, size=${_fmtBytes(fullJson.length)} (${tabCount} tab, ${totalRoots} kök obje)`);
        }
      } else if (SAVE_DEBUG) {
        console.log(`[save]   [scene-objects.json] BOŞ (serialize=${_fmtMs(_tSSDone - _tSS)})`);
      }
    } catch (err) {
      console.warn('[jbz-save] Failed to serialize scene objects:', err);
    }

    // viewer-scenes.json — DEVRE DIŞI (save hız optimizasyonu, 2026-05-19).
    // Bu dosya "Full viewer-ready scenes (includes DXF objects for preview
    // viewer)" amacıyla yazılıyordu — TÜM sahne içeriği + DXF entity'leri
    // recursive serialize ediliyor. Binlerce DXF entity'de save'in en büyük
    // yükü (dakikalar mertebesinde). Şu anda preview viewer feature'ı aktif
    // değil; gerekirse generate-on-demand olarak ayrı bir endpoint'te
    // üretilebilir. Mesh-FP refactor (Aşama 5) ile save formatı zaten
    // yeniden tasarlanacak. Bu blok kaldırıldı.
    // Eski kod referans için: git log -- src/services/projectSerializer.js
  }

  // ── Checksum: dosya sayısı + slice key listesi üzerinden hızlı hash ──
  const fileKeys = Object.keys(zip.files).filter(k => !k.endsWith('/')).sort();
  manifest.checksum = simpleChecksum(fileKeys.join('|') + '|' + nowISO());

  // Write manifest
  zip.file('manifest.json', safeJSON(manifest));

  // STORE (no compression) — JSZip compression save'in dominant maliyetiydi
  // (DXF + scene JSON için 30-60sn). Sıkıştırmasız zip, dosya boyutu 2-3x
  // büyür ama save 3-5x hızlanır. Tek dosya politikasıyla birlikte disk
  // tasarrufu da kritik değil (versiyon birikmiyor).
  _mark('manifest yazıldı, ZIP generate başlıyor');
  const _tZip = SAVE_DEBUG ? _t() : 0;
  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'STORE',
  });
  if (SAVE_DEBUG) {
    const zipDt = _t() - _tZip;
    const totalDt = _t() - _tStart;
    console.log(`[save]   [zip.generateAsync] ${_fmtMs(zipDt)}, blob size=${_fmtBytes(blob.size)}, dosya sayısı=${Object.keys(zip.files).filter(k => !k.endsWith('/')).length}`);
    console.log(`[save] === BİTTİ === toplam ${_fmtMs(totalDt)}`);
  }
  return blob;
}

// ─── Build Electrical Infrastructure (Section 10.1) ─────────────────────────

function buildElectricalInfrastructure(simpleSupply) {
  const components = simpleSupply.components || [];
  const feeders    = simpleSupply.feeders || [];

  // Find root node (supply component)
  const rootComp = components.find(c =>
    c.kind === 'supply' || c.kind === 'SUPPLY'
  );

  const nodes = {};
  components.forEach(comp => {
    const compType = KIND_TO_COMPONENT_TYPE[comp.kind?.toLowerCase()] || comp.kind || 'unknown';

    // Find children
    const childIds = feeders
      .filter(f => f.sourceId === comp.id)
      .map(f => f.targetId)
      .filter(Boolean);

    // Find parent
    const parentFeeder = feeders.find(f => f.targetId === comp.id);
    const parentId = parentFeeder ? parentFeeder.sourceId : null;

    nodes[comp.id] = {
      id:            comp.id,
      componentType: compType,
      label:         comp.label || comp.name || comp.kind || '',
      documentRef:   null,
      objectRef:     comp.sceneObjectId || null,
      electricalData: {
        totalInstalledPower: comp.installedPower || comp.power || 0,
        demandPower:         comp.demandPower || 0,
        voltage:             comp.voltage || 230,
        phases:              comp.phases || 1,
        current:             comp.current || 0,
        fuseRating:          comp.fuseRating || 0,
        fuseType:            comp.fuseType || '',
        cableCrossSection:   comp.cableCrossSection || '',
        cableType:           comp.cableType || '',
      },
      parent:   parentId,
      children: childIds,
    };
  });

  return {
    disciplineType: 'electrical_strong',
    rootNodeId:     rootComp?.id || null,
    nodes,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// DESERIALIZE (Load)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Deserialize a .jbz Blob/ArrayBuffer.
 * Supports both new (v1.0.0 spec) and old (flat slice) formats.
 *
 * @param {Blob|ArrayBuffer} file
 * @returns {Promise<{ manifest, slices, sceneObjects }>}
 */
export async function deserializeProject(file) {
  const zip = await JSZip.loadAsync(file);

  const manifestRaw = await zip.file('manifest.json')?.async('string');
  if (!manifestRaw) throw new Error('Geçersiz .jbz dosyası: manifest.json bulunamadı');
  const manifest = JSON.parse(manifestRaw);

  // ── Detect format version ──
  const isNewFormat = manifest.formatVersion && !manifest.slices;

  if (isNewFormat) {
    return deserializeNewFormat(zip, manifest);
  } else {
    return deserializeOldFormat(zip, manifest);
  }
}

// ─── New Format Deserializer ─────────────────────────────────────────────────

async function deserializeNewFormat(zip, manifest) {
  const slices = {};

  // ── Priority 1: Load from _state/ folder (raw Redux slices) ──
  const stateFolder = zip.folder('_state');
  if (stateFolder) {
    for (const filename of Object.keys(SLICE_MAP)) {
      const entry = zip.file(`_state/${filename}.json`);
      if (entry) {
        try {
          const raw = await entry.async('string');
          slices[filename] = JSON.parse(raw);
        } catch (err) {
          console.warn(`[jbz-load] Failed to parse _state/${filename}.json:`, err);
        }
      }
    }
  }

  // ── Priority 2: If _state/ is missing/incomplete, reconstruct from spec structure ──
  if (!slices['floor-plans']) {
    slices['floor-plans'] = await reconstructFloorPlansFromSpec(zip, manifest);
  }
  if (!slices['simple-supply']) {
    slices['simple-supply'] = await reconstructSupplyFromSpec(zip);
  }

  // ── Load scene objects ──
  // FAZ 2 (save-load-rewrite): YENİ format öncelikli — disiplin bazlı
  // _state/<discipline>/scene.json dosyalarından oku, eski format
  // (_state/scene-objects.json) fallback olarak kalır.
  let sceneObjects = null;
  const sceneObjectsByTab = {};
  const probeResults = {};
  // ZIP içinde hangi scene dosyaları var, tara
  const allEntries = Object.keys(zip.files || {}).filter(f => f.endsWith('scene.json') || f.endsWith('scene-objects.json'));
  // eslint-disable-next-line no-console
  console.log('[LOAD:zip-scene-files-found]', JSON.stringify(allEntries));
  // eslint-disable-next-line no-console
  console.log('[LOAD:DISCIPLINE_TO_TAB mapping]', JSON.stringify(DISCIPLINE_TO_TAB));

  for (const [discipline, tabId] of Object.entries(DISCIPLINE_TO_TAB)) {
    const path = `_state/${discipline}/scene.json`;
    const entry = zip.file(path);
    probeResults[path] = entry ? 'BULUNDU' : 'YOK';
    if (!entry) continue;
    try {
      const data = JSON.parse(await entry.async('string'));
      if (data?.children && Array.isArray(data.children)) {
        sceneObjectsByTab[tabId] = data.children;
        probeResults[path] = `BULUNDU childrenCount=${data.children.length} → tabId=${tabId}`;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(`[LOAD:parse-FAILED] ${path}`, err);
    }
  }
  // eslint-disable-next-line no-console
  console.log('[LOAD:per-discipline-probe]', JSON.stringify(probeResults));

  if (Object.keys(sceneObjectsByTab).length > 0) {
    sceneObjects = sceneObjectsByTab;
    // eslint-disable-next-line no-console
    console.log('[LOAD:using-new-format]', JSON.stringify({
      keys: Object.keys(sceneObjectsByTab),
      counts: Object.fromEntries(Object.entries(sceneObjectsByTab).map(([k, v]) => [k, v.length])),
    }));
  } else {
    // Eski format fallback
    const sceneEntry = zip.file('_state/scene-objects.json');
    if (sceneEntry) {
      try {
        sceneObjects = JSON.parse(await sceneEntry.async('string'));
        // eslint-disable-next-line no-console
        console.log('[LOAD:using-old-format] scene-objects.json keys=', Object.keys(sceneObjects || {}));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('[LOAD:old-format-parse-FAILED]', err);
      }
    } else {
      // eslint-disable-next-line no-console
      console.log('[LOAD:NO-SCENE-DATA] hiç scene dosyası okunamadı!');
    }
  }

  // ── Read meta.json for additional project info ──
  let metaJson = null;
  const metaEntry = zip.file('meta.json');
  if (metaEntry) {
    try {
      metaJson = JSON.parse(await metaEntry.async('string'));
    } catch (err) {
      console.warn('[jbz-load] Failed to parse meta.json:', err);
    }
  }

  // ── Enrich project-type from meta.json if not in _state ──
  // Prefer explicit editorTemplate (A/B/C/D) over legacy project.type which may be a domain label
  if (!slices['project-type']) {
    const tpl = metaJson?.project?.editorTemplate;
    const VALID = ['SINGLE_UNIT', 'MULTI_FLOOR_SINGLE_METER', 'MULTI_METER', 'HIGH_RISE'];
    if (tpl && VALID.includes(tpl)) {
      slices['project-type'] = {
        projectType: tpl,
        selectedDocuments: getDefaultDocuments(tpl),
        autoDetect: false,
      };
    } else if (metaJson?.project?.type) {
      slices['project-type'] = { type: metaJson.project.type };
    }
  }

  // ── Enrich settings from meta.json if not in _state ──
  if (!slices['settings'] && metaJson?.settings) {
    slices['settings'] = metaJson.settings;
  }

  // ── Restore project tabs active state from manifest ──
  if (!slices['project-tabs'] && manifest.activeState) {
    const activeDiscipline = manifest.activeState.activeDiscipline || 'architectural';
    const activeTab = DISCIPLINE_TO_TAB[activeDiscipline] || 'mimari';
    const enabledTabs = (manifest.disciplines || [])
      .filter(d => d.include)
      .map(d => DISCIPLINE_TO_TAB[d.code])
      .filter(Boolean);
    if (!enabledTabs.includes('mimari')) enabledTabs.unshift('mimari');

    slices['project-tabs'] = {
      activeTabId: activeTab,
      enabledPaftaIds: enabledTabs,
    };
  }

  return { manifest, slices, sceneObjects };
}

// ─── Old Format Deserializer (backward compat) ──────────────────────────────

async function deserializeOldFormat(zip, manifest) {
  const slices = {};
  for (const filename of Object.keys(SLICE_MAP)) {
    const entry = zip.file(`${filename}.json`);
    if (entry) {
      try {
        const raw = await entry.async('string');
        slices[filename] = JSON.parse(raw);
      } catch (err) {
        console.warn(`[jbz-load] Failed to parse ${filename}.json:`, err);
      }
    }
  }

  let sceneObjects = null;
  const sceneEntry = zip.file('scene-objects.json');
  if (sceneEntry) {
    try {
      sceneObjects = JSON.parse(await sceneEntry.async('string'));
    } catch (err) {
      console.warn('[jbz-load] Failed to parse scene-objects.json:', err);
    }
  }

  return { manifest, slices, sceneObjects };
}

// ─── Spec → Redux Reconstruction Helpers ─────────────────────────────────────

async function reconstructFloorPlansFromSpec(zip, manifest) {
  const tabs = {};

  // Reconstruct from disciplines/*/documents/
  const disciplines = manifest.disciplines || [];
  for (const disc of disciplines) {
    if (!disc.include) continue;
    const tabId = DISCIPLINE_TO_TAB[disc.code];
    if (!tabId) continue;

    const docFolder = `disciplines/${disc.code}/documents/`;
    const docFiles = Object.keys(zip.files).filter(
      f => f.startsWith(docFolder) && f.endsWith('.json')
    );

    const byId = {};
    const allIds = [];
    for (const docPath of docFiles) {
      try {
        const raw = await zip.file(docPath)?.async('string');
        if (!raw) continue;
        const docJson = JSON.parse(raw);
        const docId = docJson.documentId || docJson._appData?.id || docPath.split('/').pop().replace('.json', '');

        // Reconstruct floor plan entry from document + _appData
        const appData = docJson._appData || {};
        const plan = {
          id: docId,
          name: docJson.title || 'Doküman',
          type: appData.type || 'floor',
          number: appData.number || 0,
          floorCount: appData.floorCount || 1,
          draw: appData.draw || [],
          width: docJson.frame?.width || 841,
          height: docJson.frame?.height || 594,
          objects: [],
          objectsIds: appData.objectsIds || [],
          sourceFloorPlanId: appData.sourceFloorPlanId || null,
        };

        byId[docId] = plan;
        allIds.push(docId);
      } catch (err) {
        console.warn(`[jbz-load] Failed to reconstruct doc from ${docPath}:`, err);
      }
    }

    if (allIds.length > 0) {
      tabs[tabId] = { byId, allIds, activeId: allIds[0] };
    }
  }

  // Reconstruct mimari from floor-plans/
  if (!tabs.mimari) {
    const fpFolder = 'floor-plans/';
    const fpFiles = Object.keys(zip.files).filter(
      f => f.startsWith(fpFolder) && f.endsWith('.json')
    );

    const byId = {};
    const allIds = [];
    for (const fpPath of fpFiles) {
      try {
        const raw = await zip.file(fpPath)?.async('string');
        if (!raw) continue;
        const fpJson = JSON.parse(raw);
        const fpId = fpJson.floorId || fpPath.split('/').pop().replace('.json', '');

        const plan = {
          id: fpId,
          name: fpJson.label || 'Kat',
          type: fpJson.type || 'floor',
          number: fpJson.number || 0,
          floorCount: fpJson.floorCount || 1,
          draw: fpJson.draw || [],
          width: fpJson.frame?.width || 2400,
          height: fpJson.frame?.height || 1600,
          objects: [],
          objectsIds: [],
          sourceFloorPlanId: fpJson.sourceFloorPlanId || null,
        };

        byId[fpId] = plan;
        allIds.push(fpId);
      } catch (err) {
        console.warn(`[jbz-load] Failed to reconstruct floor plan from ${fpPath}:`, err);
      }
    }

    if (allIds.length > 0) {
      tabs.mimari = { byId, allIds, activeId: allIds[0] };
    }
  }

  return {
    activeTabId: 'mimari',
    tabs,
  };
}

async function reconstructSupplyFromSpec(zip) {
  const infraEntry = zip.file('disciplines/electrical/infrastructure.json');
  if (!infraEntry) return { feeders: [], components: [], metadata: {} };

  try {
    const raw = await infraEntry.async('string');
    const infra = JSON.parse(raw);

    const components = Object.values(infra.nodes || {}).map(node => ({
      id: node.id,
      kind: node.componentType,
      label: node.label,
      parentId: node.parent,
      ...(node.electricalData || {}),
    }));

    return { feeders: [], components, metadata: { version: '1.0' } };
  } catch (err) {
    console.warn('[jbz-load] Failed to reconstruct supply from infrastructure:', err);
    return { feeders: [], components: [], metadata: {} };
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// HYDRATION (Convert deserialized slices → Redux payload)
// ═══════════════════════════════════════════════════════════════════════════════

// Maps .jbz file keys → Redux store keys
const FILE_TO_SLICE = {
  'dxf-data':         'dxf',
  'layers':           'layer',
  'simple-supply':    'simpleSupply',
  'wires':            'wires',
  'floor-plans':      'floorPlans',
  'grounding':        'grounding',
  'baslik':           'baslik',
  // baslik-templates: artık projeye gömülmüyor (kullanıcı-seviyesi, backend'de)
  'panel-config':     'panelConfig',
  'panel-detail':     'panelDetail',
  'energy-room':      'energyRoom',
  'project-type':     'projectType',
  'settings':         'settings',
  'mods':             'mods',
  'project-tabs':     'projectTabs',
  'tesisat':          'tesisat',
  'utils':            'utils',
};

/**
 * Kaydedilmiş panelConfig'i güncel default'larla merge et.
 * Amaç: eski projeler açılınca yeni eklenen kategoriler / output tipleri /
 * default output'lar (örn. "Motor Yükleri") kaybolmasın. Kullanıcı
 * özelleştirmeleri (saved) korunur, üzerine eksik default'lar eklenir.
 * defaultOutputs union'lanır (saved sıra korunur, eksik default'lar sona eklenir);
 * menü flyout sırası category.order'a göre olduğu için ekleme sırası önemsiz.
 */
function mergePanelConfigWithDefaults(saved) {
  if (!saved || typeof saved !== 'object') return saved;

  // Kategoriler kullanıcı-özelleştirilemez (CRUD action yok) → kayıtlı kategoriler
  // eski default snapshot'ı; label/güncelleme yeni default'tan gelsin (defaults kazanır).
  const outputCategories = { ...(saved.outputCategories || {}), ...OUTPUT_CATEGORIES };
  const outputTypes = { ...OUTPUT_TYPES, ...(saved.outputTypes || {}) };

  const panelTypes = { ...PANEL_TYPES };
  for (const [id, savedPt] of Object.entries(saved.panelTypes || {})) {
    const defPt = PANEL_TYPES[id];
    if (!defPt) { panelTypes[id] = savedPt; continue; }
    const savedOuts = Array.isArray(savedPt.defaultOutputs) ? savedPt.defaultOutputs : [];
    const missingDefaults = (defPt.defaultOutputs || []).filter(o => !savedOuts.includes(o));
    panelTypes[id] = { ...defPt, ...savedPt, defaultOutputs: [...savedOuts, ...missingDefaults] };
  }

  return {
    ...saved,
    panelTypes,
    outputTypes,
    outputCategories,
    customizations: saved.customizations || {},
  };
}

/**
 * Convert deserialized file-keyed slices to Redux slice-keyed payload.
 * Ready to be dispatched as hydrateProject(payload).
 */
export function toHydratePayload(slices) {
  const payload = {};
  for (const [filename, data] of Object.entries(slices)) {
    const sliceKey = FILE_TO_SLICE[filename];
    if (sliceKey && data) {
      // Backwards compat: old flat floor-plans format → new per-tab format
      if (sliceKey === 'floorPlans' && data.byId && !data.tabs) {
        payload[sliceKey] = {
          activeTabId: 'mimari',
          tabs: {
            mimari: {
              byId: data.byId,
              allIds: data.allIds || [],
              activeId: data.activeId || null,
            },
          },
        };
      } else if (sliceKey === 'panelConfig') {
        // Eski projelerde yeni eklenen kategoriler/output'lar kaybolmasın
        payload[sliceKey] = mergePanelConfigWithDefaults(data);
      } else {
        payload[sliceKey] = data;
      }
    }
  }

  // ── Reconstruct sitePlan Three.js Group from _serializedGroup ──
  if (payload.utils?.sitePlan?._serializedGroup) {
    try {
      payload.utils.sitePlan.group = deserializeFloorPlanGroup(payload.utils.sitePlan._serializedGroup);
    } catch (err) {
      console.warn('[jbz-load] Failed to rebuild sitePlan group:', err);
      payload.utils.sitePlan.group = null;
    }
    delete payload.utils.sitePlan._serializedGroup;
  }

  // ── Reconstruct floor plan Three.js Groups from _serializedGroup ──
  // İki aşamalı: önce mimari tab'ı deserialize (gerçek group'lar burada),
  // sonra non-mimari tab'lar mimari'den sourceFloorPlanId üzerinden referans
  // alır. Memory optimizasyonu — save akışındaki cross-tab dedup ile
  // simetrik (kullanıcı Out of Memory hatası aldığı için zorunlu fix).
  if (payload.floorPlans?.tabs) {
    const mimariTab = payload.floorPlans.tabs.mimari;
    // 1. Mimari tab'ı deserialize
    if (mimariTab?.byId) {
      for (const [fpId, plan] of Object.entries(mimariTab.byId)) {
        if (plan._serializedGroup) {
          try {
            plan.group = deserializeFloorPlanGroup(plan._serializedGroup);
          } catch (err) {
            console.warn(`[jbz-load] Failed to rebuild group for mimari/${fpId}:`, err);
            plan.group = null;
          }
          delete plan._serializedGroup;
        }
      }
    }
    // 2. Non-mimari tab'lar: sourceFloorPlanId üzerinden mimari group'u paylaş
    for (const [tabId, tabData] of Object.entries(payload.floorPlans.tabs)) {
      if (tabId === 'mimari' || !tabData?.byId) continue;
      for (const [fpId, plan] of Object.entries(tabData.byId)) {
        if (plan._serializedGroup) {
          // Legacy: eski JBZ'lerde cross-tab _serializedGroup var olabilir.
          try {
            plan.group = deserializeFloorPlanGroup(plan._serializedGroup);
          } catch (err) {
            console.warn(`[jbz-load] Failed to rebuild group for ${tabId}/${fpId}:`, err);
            plan.group = null;
          }
          delete plan._serializedGroup;
        } else if (plan.sourceFloorPlanId && mimariTab?.byId?.[plan.sourceFloorPlanId]) {
          // Yeni format: cross-tab plan group reference paylaşımı
          plan.group = mimariTab.byId[plan.sourceFloorPlanId].group || null;
        }
      }
    }
  }

  return payload;
}
